import { useState } from "react";
import { Button, IconButton, InputAdornment, ListItemIcon, Menu, MenuItem, TextField, Tooltip, useMediaQuery } from "@mui/material";
import { ClearRounded, MoreVert } from "@mui/icons-material";
import { Calendar, Search } from "react-feather";
import { DateRangePicker } from "rsuite";
import styles from "./toolbar.module.css";

/**
 * One-row toolbar for the booking / delivery work lists (assign driver, driver jobs, receive shipping):
 *   [search][date range] ........ [view switch][3 selected · Clear][Secondary][Main action][⋯ menu]
 * On phones: search on its own row, then the rest; while rows are ticked the main action moves to a bar
 * stuck to the bottom of the screen.
 *
 * primaryAction / secondaryAction: { label, icon, onClick, disabled, hint }  (hint = tooltip while disabled)
 * menuItems: [{ label, icon, onClick, disabled }]
 * compact: for half-width panels (search and date share the row, the action stays on the right)
 */
const ListToolbar = ({
  search, onSearch, searchPlaceholder = "Search booking ID, customer, postcode...",
  dateRange, onDateRange, showDate = true,
  viewSwitch, selectedCount = 0, onClearSelection,
  primaryAction, secondaryAction, menuItems = [], compact = false,
}) => {
  const isPhone = useMediaQuery("(max-width:768px)");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const visibleMenu = menuItems.filter(Boolean);

  const actionButton = (action, variant) => {
    if (!action) return null;
    const button = (<Button
      className={variant === "primary" ? styles.PrimaryBU : styles.SecondaryBU}
      onClick={action.onClick}
      disabled={action.disabled}
      startIcon={action.icon}
    >
      {action.label}
    </Button>);
    // Tooltips need a wrapper element to show on a disabled button
    return action.disabled && action.hint ? <Tooltip title={action.hint}><span className={styles.TooltipWrap}>{button}</span></Tooltip> : button;
  };

  const selection = selectedCount > 0 && (<span className={styles.Selection}>
    <strong>{selectedCount}</strong> selected
    {onClearSelection && <button type="button" className={styles.ClearBU} onClick={onClearSelection}>Clear</button>}
  </span>);

  // Phones: the main action sits in a bottom bar while something is ticked
  const stickyAction = isPhone && primaryAction && selectedCount > 0;

  return (<>
    <div className={`${styles.Toolbar} ${compact ? styles.Compact : ""}`}>
      <div className={styles.Filters}>
        {onSearch && (<div className={styles.Search}>
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={search || ""}
            onChange={(e) => onSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search size={16} className={styles.SearchIcon} /></InputAdornment>,
              endAdornment: search ? (<InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => onSearch("")}><ClearRounded fontSize="small" /></IconButton>
              </InputAdornment>) : null,
            }}
          />
        </div>)}
        {showDate && onDateRange && (<div className={styles.Date}>
          <DateRangePicker
            format="dd/MM/yyyy"
            placement="bottomEnd"
            placeholder="Filter by date"
            caretAs={Calendar}
            value={dateRange || null}
            showOneCalendar
            block
            onChange={onDateRange}
          />
        </div>)}
      </div>

      <div className={styles.Actions}>
        {viewSwitch}
        {!isPhone && selection}
        {actionButton(secondaryAction, "secondary")}
        {!stickyAction && actionButton(primaryAction, "primary")}
        {visibleMenu.length > 0 && (<>
          <Tooltip title="More actions">
            <IconButton className={styles.MoreBU} onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More actions" aria-haspopup="menu">
              <MoreVert />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
            {visibleMenu.map((item) => (<MenuItem key={item.label} disabled={item.disabled} onClick={() => { setMenuAnchor(null); item.onClick(); }}>
              {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
              {item.label}
            </MenuItem>))}
          </Menu>
        </>)}
      </div>
    </div>

    {stickyAction && (<div className={styles.StickyBar}>
      {selection}
      {actionButton(primaryAction, "primary")}
    </div>)}
  </>);
};

export default ListToolbar;
